import { Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AccusedRecord, CriminalDocumentRow, CriminalWizardDraft } from '../types/criminalCaseRequest';
import { FILE_CATEGORY_CRIMINAL } from '../mocks/criminalMasterData';
import { CRIMINAL_CATEGORY_CODE, CRIMINAL_CATEGORY_ID } from '../pages/wizard/criminal/criminalConstants';
import * as criminalService from '../services/criminalCaseRequests';
import { isDocumentTypeUuid } from '../utils/documentTypeId';

function newAccused(): AccusedRecord {
  return { id: crypto.randomUUID(), name: '', status: '', statusOther: '' };
}

export const DEFAULT_CRIMINAL_DRAFT: CriminalWizardDraft = {
  fileCategory: FILE_CATEGORY_CRIMINAL,
  fileCategoryId: '',
  subFileCategoryId: '',
  subFileCategoryName: '',
  applicableAreas: [],
  applicableAreaIds: [],
  instituteType: 'government',
  corporateInstitute: '',
  corporateInstituteName: '',
  corporateInstituteOther: '',
  corporateInstituteContactName: '',
  corporateInstitutePrefix: '',

  description: '',
  caseSourceId: '',
  caseSource: '',
  caseSourceOther: '',
  provinceId: '',
  provinceName: '',
  districtId: '',
  districtName: '',
  highCourtId: '',
  highCourtName: '',
  magistrateCourtId: '',
  magistrateCourtName: '',
  policeStationId: '',
  policeStationName: '',
  yourReferenceNumber: '',
  otherExternalReferences: '',
  caseNumber: '',
  hasConnectedMatters: 'no',
  connectedCaseNumber: '',
  connectedAgdFileRefAvailable: false,
  connectedAgdFileRef: '',

  victimName: '',
  victimDob: '',
  victimGender: '',
  victimGenderId: '',

  offenceDateFrom: '',
  offenceDateTo: '',
  placeOfOffence: '',
  accusedDetailsAvailable: true,
  accused: [newAccused()],
  urgencyLevelId: '',
  urgencyLevelName: '',
  deadline: '',
  urgencyJustification: '',

  deliveryMethodIds: [],
  deliveryMethods: [],
  deliveryOther: '',

  documents: [],
  confirmCorrect: false,

  draftRequestId: '',
  referenceNo: '',
};

export type CriminalWizardContextValue = {
  draft: CriminalWizardDraft;
  setDraft: React.Dispatch<React.SetStateAction<CriminalWizardDraft>>;
  reset: () => void;
};

type WizardSeedState = { seedDraft?: Partial<CriminalWizardDraft> };

export default function CriminalRequestLayout() {
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const resumeId = (params.caseRequestId ?? searchParams.get('id') ?? '').trim();
  const [draft, setDraft] = useState<CriminalWizardDraft>({ ...DEFAULT_CRIMINAL_DRAFT, accused: [newAccused()] });
  const [loadingDraft, setLoadingDraft] = useState(Boolean(resumeId));
  const preselectedCategory = useRef(false);

  useEffect(() => {
    const seed = (location.state as WizardSeedState | null)?.seedDraft;
    if (!seed) return;
    setDraft((d) => ({ ...DEFAULT_CRIMINAL_DRAFT, ...d, ...seed, accused: d.accused?.length ? d.accused : [newAccused()] }));
    window.history.replaceState({}, document.title);
  }, [location.state]);
  // File Category must be user-selected before showing wizard steps.
  // Keep draft.fileCategoryId empty by default, except when resuming an existing draft.
  useEffect(() => {
    if (resumeId || preselectedCategory.current) return;
    preselectedCategory.current = true;
  }, [resumeId]);

  useEffect(() => {
    if (!resumeId) {
      setLoadingDraft(false);
      return;
    }
    let mounted = true;
    void criminalService.getCriminalWizardDraft(resumeId)
      .then((r) => {
        if (!mounted) return;
        if (r.success && r.data) {
          setDraft({ ...DEFAULT_CRIMINAL_DRAFT, ...r.data, accused: r.data.accused?.length ? r.data.accused : [newAccused()] });
        }
      })
      .catch(() => {
        // Ignore — show empty draft shell if load fails.
      })
      .finally(() => {
        if (mounted) setLoadingDraft(false);
      });
    return () => {
      mounted = false;
    };
  }, [resumeId]);

  const v = useMemo<CriminalWizardContextValue>(
    () => ({
      draft,
      setDraft,
      reset: () => setDraft({ ...DEFAULT_CRIMINAL_DRAFT, accused: [newAccused()] }),
    }),
    [draft],
  );

  if (loadingDraft) {
    return <div className="pg-sub">Loading case request…</div>
  }

  return <Outlet context={v} />;
}

function normalizeDocLabel(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function findDocumentForChecklistItem(
  prev: CriminalDocumentRow[],
  item: { id: string; documentTypeId: string; name: string; documentTypeCode?: string },
): CriminalDocumentRow | undefined {
  return prev.find((d) => {
    if (d.checklistItemId === item.id || d.documentTypeId === item.documentTypeId) return true;
    if (item.documentTypeCode) {
      if (d.documentTypeId === item.documentTypeCode || d.checklistItemId === item.documentTypeCode) return true;
    }
    if (!isDocumentTypeUuid(d.documentTypeId) && normalizeDocLabel(d.documentName) === normalizeDocLabel(item.name)) {
      return true;
    }
    return false;
  });
}

export function syncDocumentsFromChecklist(
  prev: CriminalDocumentRow[],
  checklist: { id: string; documentTypeId: string; name: string; mandatory: boolean; documentTypeCode?: string }[],
): CriminalDocumentRow[] {
  return checklist.map((item) => {
    const existing = findDocumentForChecklistItem(prev, item);
    if (existing) {
      return {
        ...existing,
        checklistItemId: item.id,
        documentTypeId: item.documentTypeId,
        documentName: item.name,
        mandatory: item.mandatory,
      };
    }
    return {
      id: crypto.randomUUID(),
      checklistItemId: item.id,
      documentTypeId: item.documentTypeId,
      documentName: item.name,
      mandatory: item.mandatory,
      fileName: '',
      originalFileName: '',
      uploadTime: '',
      skipReason: '',
    };
  });
}
